import React, { useState, useRef, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import { theme } from "./table";
import { Button, Stack } from "@mui/material";
import { useContextMenu } from "./table";
import { nanoid } from "nanoid";
import toast from "react-hot-toast";
import SectionHeader from "../SectionHeader";
const EditorTable = ({
  data,
  save,
  close,
  loading,
  noColumnMod = false,
  noRowMod = false,
  noHeader = false,
  cellFlex = false,
  clearButtonTitle = "Cancel",
  noClearButton = false,
}) => {
  const { ContextMenu, handleContextMenu } = useContextMenu();
  let cellRenderer = (props) => {
    return (
      <span
        style={{
          height: "100%",
          width: "110%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          fontWeight: props.node.rowIndex === 0 ? "bold" : "normal",
        }}
        {...(props.node.rowIndex === 0 && noColumnMod
          ? { onContextMenu: (event) => event.preventDefault() }
          : { onContextMenu: (event) => handleContextMenu(event, props) })}
        // onContextMenu={(event) => handleContextMenu(event, props)}
      >
        {props.data[props.column.getColId()]}
      </span>
    );
  };
  const [dirty, setDirty] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [rowData, setRowData] = useState([{ col1: "" }]);
  const [columnDefs, setColumnDefs] = useState([
    {
      field: "col1",
      editable: true,
      cellRenderer,
      sortable: true,
      suppressKeyboardEvent: (params) => {
        if (
          params.event.key === "Tab" &&
          params.event.target.tagName === "INPUT"
        ) {
          params.event.preventDefault();
          params.api.stopEditing(false);
          setTimeout(() => params.api.tabToNextCell(), 0);
          return true;
        }
        return false;
      },
    },
  ]);
  useEffect(() => {
    if (!data?.length) return;
    let map = data[0].reduce((a, c) => {
      a[c] = nanoid();
      return a;
    }, {});
    let headers = data[0].map((v, i) => {
      return {
        field: map[v],
        editable: true,
        cellRenderer,
        sortable: true,
        suppressKeyboardEvent: (params) => {
          if (
            params.event.key === "Tab" &&
            params.event.target.tagName === "INPUT"
          ) {
            params.event.preventDefault();
            params.api.stopEditing(false);
            setTimeout(() => params.api.tabToNextCell(), 0);
            return true;
          }
          return false;
        },
        filterable: true,
        ...(cellFlex ? { flex: true } : {}),
      };
    });
    let headerRow = Object.values(data[0]).reduce((a, c, i) => {
      a[map[c]] = c;
      return a;
    }, {});
    const rows = [
      headerRow,
      ...data.slice(1).map((d) =>
        Object.entries(d).reduce((a, [k, v], i) => {
          a[map[k]] = v;
          return a;
        }, {})
      ),
    ];
    setColumnDefs(headers);
    setRowData(rows);
    setRefresh((s) => ++s);
    setDirty(false);
  }, [data]);
  const gridApiRef = useRef(null);
  const onGridReady = (params) => {
    gridApiRef.current = params.api;
    window.grid = params.api;
  };
  const onCellValueChanged = (params) => {
    setRowData((rows) => {
      return rows.map((row, index) => {
        if (!params.rowIndex !== index) return row;
        row[params.column.colId] = params.newValue;
      });
    });
    setDirty(true);
  };
  const saveSheet = () => {
    let header = rowData[0];
    if (
      !Object.values(header).every((x) => x) ||
      Object.values(header).length !== columnDefs.length
    )
      return toast.error("Headers must exist for each column");
    let rows = rowData.slice(1);
    let parsedRows = rows.map((row) => {
      return Object.entries(row).reduce((a, [k, v], index) => {
        a[header[k]] = v;
        return a;
      }, {});
    });
    let csvData = [Object.values(header), ...parsedRows];
    save(csvData);
    setDirty(false);
  };
  return (
    <div
      className="ag-theme-alpine"
      style={{
        height: "400px",
        "--ag-cell-horizontal-border": "1px solid #e2e2e2",
        "--ag-cell-vertical-border": "1px solid #e2e2e2",
      }}
    >
      {!noHeader ? (
        <SectionHeader icon={"border_all"}>Sheet Editor</SectionHeader>
      ) : null}
      <Stack direction="row" mt={2} mb={1}>
        {!noColumnMod ? (
          <Button
            onClick={() => {
              setColumnDefs((d) => {
                return [
                  ...d,
                  {
                    field: nanoid(),
                    editable: true,
                    cellRenderer,
                    sortable: true,
                    suppressKeyboardEvent: (params) => {
                      if (
                        params.event.key === "Tab" &&
                        params.event.target.tagName === "INPUT"
                      ) {
                        params.event.preventDefault();
                        params.api.stopEditing(false);
                        setTimeout(() => params.api.tabToNextCell(), 0);
                        return true;
                      }
                      return false;
                    },
                    ...(cellFlex ? { flex: true } : {}),
                  },
                ];
              });
              setRefresh((s) => ++s);
            }}
            variant="contained"
          >
            Add Column
          </Button>
        ) : null}
        {!noRowMod ? (
          <Button
            onClick={() => {
              setRowData((d) => {
                return [
                  ...d,
                  columnDefs.reduce((a, c) => {
                    a[c.field] = "";
                    return a;
                  }, {}),
                ];
              });
            }}
            variant="contained"
          >
            Add Row
          </Button>
        ) : null}
        {!noClearButton ? (
          <Button
            sx={{ marginLeft: "auto" }}
            onClick={close}
            variant="contained"
          >
            {clearButtonTitle}
          </Button>
        ) : null}
        <Button
          sx={{ marginLeft: noClearButton ? "auto" : "10px" }}
          onClick={saveSheet}
          variant="contained"
          {...(dirty ? { color: "secondary" } : {})}
        >
          Save
        </Button>
      </Stack>
      <AgGridReact
        key={refresh}
        rowData={rowData}
        columnDefs={columnDefs}
        onGridReady={onGridReady}
        onCellValueChanged={onCellValueChanged}
        ref={gridApiRef}
        theme={theme}
        loading={loading}
        gridOptions={{ headerHeight: 0 }}
      />
      {!noRowMod || !noColumnMod ? (
        <ContextMenu
          items={[
            ...(!noRowMod
              ? [
                  {
                    title: "Delete Row",
                    handler: (row, column, value, props) => {
                      if (rowData.length === 1) return;
                      let index = props?.node?.childIndex;
                      let newRows = rowData.filter((r, i) => i !== index);
                      setRowData(newRows);
                      setRefresh((n) => ++n);
                    },
                  },
                ]
              : []),
            ...(!noColumnMod
              ? [
                  {
                    title: "Delete Column",
                    handler: (row, column, value, props) => {
                      if (columnDefs.length === 1) return;
                      let newCols = columnDefs.filter(
                        (c, i) => c.field !== column
                      );
                      let newRows = rowData.map((r) => {
                        delete r[column];
                        return r;
                      });
                      setColumnDefs(newCols);
                      setRowData(newRows);
                      setRefresh((n) => ++n);
                    },
                  },
                ]
              : []),
          ]}
        />
      ) : null}
    </div>
  );
};

export default EditorTable;
